<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { useUI } from '@/composables/useUI'
import { usePaginacao } from '@/composables/usePaginacao'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()
const { obterClasseStatusGhost } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

// --- Estado Local ---
const parceiros = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'id', label: 'ID', width: 80, sortable: true, slotName: 'idParceiro' },
  { field: 'apelido', label: 'Apelido', sortable: true, class: 'fw-medium' },
  { field: 'nomeFantasia', label: 'Nome Fantasia', sortable: true },
  { field: 'documento', label: 'Documento', width: 180, sortable: true },
  { field: 'funcoes', label: 'Funções', sortable: false, slotName: 'funcoes' },
  { field: 'lstStatus', label: 'Status', width: 120, align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 150, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstStatus: (row) => getStatusTexto(row.lstStatus)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

// Estado para o Modal de Auditoria
const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({
  tiposPessoa: [],
  status: []
})

const filtros = reactive({
  busca: '',
  lstTipoPessoa: '',
  isCliente: false,
  isFornecedor: false,
  isTransportadora: false,
  isInstFinanceira: false,
  isGrupoEmpresa: false,
  isOrgaoGoverno: false,
  isVendedor: false
})

// --- Permissões (RBAC) ---
const temPermissaoCompleta = computed(() => authStore.pode('PARCEIROS_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('PARCEIROS_VISUALIZACAO'))

const podeVerAcoes = computed(() => {
  return temPermissaoCompleta.value || temPermissaoVisualizacao.value
})

// --- Watchers Essenciais (Isolamento Multi-Tenant) ---
watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    parceiros.value = []
    paginacao.value.TotalCount = 0
  }
})

// --- Métodos ---
const carregarListas = async () => {
  try {
    const [resTipo, resStatus] = await Promise.all([
      api.get('/ListasCliente/lookup/PARCEIRO_TIPO'),
      api.get('/ListasCliente/lookup/ST')
    ])
    listas.tiposPessoa = resTipo.data
    listas.status = resStatus.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const getStatusTexto = (idStatus) => {
  const statusObj = listas.status.find(s => s.idListaClienteItem === idStatus)
  return statusObj ? statusObj.textoLista : idStatus
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página para listar os parceiros.'
    parceiros.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) {
      params.append('idEmpresa', authStore.empresaAtiva)
    }

    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstTipoPessoa) params.append('lstTipoPessoa', filtros.lstTipoPessoa)
    if (filtros.isCliente) params.append('isCliente', 'true')
    if (filtros.isFornecedor) params.append('isFornecedor', 'true')
    if (filtros.isTransportadora) params.append('isTransportadora', 'true')
    if (filtros.isInstFinanceira) params.append('isInstFinanceira', 'true')
    if (filtros.isGrupoEmpresa) params.append('isGrupoEmpresa', 'true')
    if (filtros.isOrgaoGoverno) params.append('isOrgaoGoverno', 'true')
    if (filtros.isVendedor) params.append('isVendedor', 'true')

    const response = await api.get(`/Parceiros?${params.toString()}`)

    parceiros.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar parceiros:', error)
    erro.value = 'Não foi possível carregar os dados dos parceiros.'
    parceiros.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstTipoPessoa = ''
  filtros.isCliente = false
  filtros.isFornecedor = false
  filtros.isTransportadora = false
  filtros.isInstFinanceira = false
  filtros.isGrupoEmpresa = false
  filtros.isOrgaoGoverno = false
  filtros.isVendedor = false
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarParceiro = () => {
  emit('adicionar-item')
}

const editarParceiro = async (parceiroResumo) => {
  try {
    loading.value = true
    const response = await api.get(`/Parceiros/${parceiroResumo.idParceiro}`)
    emit('editar-item', response.data)
  } catch (error) {
    console.error('Erro ao buscar dados completos do parceiro:', error)
    erro.value = 'Não foi possível carregar os detalhes do parceiro para edição.'
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (parceiro) => {
  auditoriaSelecionada.id = parceiro.idParceiro
  auditoriaSelecionada.nome = parceiro.nomeFantasia || parceiro.apelido || `Parceiro (ID: ${parceiro.idParceiro})`
  mostrarAuditoria.value = true
}

// --- Inicialização ---
onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    titulo="Parceiros de Negócio"
    subtitulo="Gestão centralizada de Clientes, Fornecedores e Transportadoras"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarParceiro"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label for="filtro-busca" class="form-label">Busca Livre (Nome, Apelido, Doc)</label>
          <input type="text" id="filtro-busca" class="form-control" v-model="filtros.busca" placeholder="Digite para buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>

        <div class="col-md-2">
          <label for="filtro-tipo" class="form-label">Tipo de Pessoa</label>
          <select id="filtro-tipo" class="form-select" v-model="filtros.lstTipoPessoa" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="tipo in listas.tiposPessoa" :key="tipo.idListaClienteItem" :value="tipo.idListaClienteItem">
              {{ tipo.textoLista }}
            </option>
          </select>
        </div>

        <div class="col-md-4">
          <label class="form-label d-block">Funções</label>
          <div class="d-flex flex-wrap gap-3 mt-2">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-cli" v-model="filtros.isCliente">
              <label class="form-check-label" for="f-cli">Cliente</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-forn" v-model="filtros.isFornecedor">
              <label class="form-check-label" for="f-forn">Fornecedor</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-trans" v-model="filtros.isTransportadora">
              <label class="form-check-label" for="f-trans">Transportadora</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-fin" v-model="filtros.isInstFinanceira">
              <label class="form-check-label" for="f-fin">Financeira</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-grp" v-model="filtros.isGrupoEmpresa">
              <label class="form-check-label" for="f-grp">Grupo/Empresa</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-gov" v-model="filtros.isOrgaoGoverno">
              <label class="form-check-label" for="f-gov">Órgão Governo</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-vend" v-model="filtros.isVendedor">
              <label class="form-check-label" for="f-vend">Vendedor</label>
            </div>
          </div>
        </div>

        <div class="col-12 mt-3 d-flex justify-content-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-exclamation-triangle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="parceiros"
      :loading="loading"
      empty-message="Nenhum parceiro de negócio encontrado."
      row-key="idParceiro"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarParceiro($event.row)"
    >
      <template #idParceiro="{ row }">
        <span class="text-muted fw-medium">#{{ row.idParceiro }}</span>
      </template>

      <template #funcoes="{ row }">
        <span v-if="row.isCliente" class="badge bg-primary me-1" title="Cliente">Cli</span>
        <span v-if="row.isFornecedor" class="badge bg-success me-1" title="Fornecedor">For</span>
        <span v-if="row.isTransportadora" class="badge bg-warning text-dark me-1" title="Transportadora">Tra</span>
        <span v-if="row.isInstFinanceira" class="badge bg-info text-dark me-1" title="Inst. Financeira">Fin</span>
        <span v-if="row.isGrupoEmpresa" class="badge bg-dark me-1" title="Grupo/Empresa">Grp</span>
        <span v-if="row.isOrgaoGoverno" class="badge bg-secondary me-1" title="Órgão Governo">Gov</span>
        <span v-if="row.isVendedor" class="badge bg-danger me-1" title="Vendedor">Ven</span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" title="Editar" @click="editarParceiro(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Parceiro"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    :tabelas-filhas="['ParceiroEndereco', 'ParceiroContaBancaria', 'ParceiroTransportadoraModal']"
    chave-estrangeira="IdParceiro"
    :dicionarios="{
      'lstTipoPessoa': listas.tiposPessoa,
      'tipoPessoa': listas.tiposPessoa,
      'lstStatus': listas.status,
      'status': listas.status
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
